import { Component, OnInit } from '@angular/core';
import { BackendApiService, RegisterUserRequest } from 'src/app/shared/services/backend-api.service';
import { AuthService } from 'src/app/shared/services/auth.service';
import { ServiceResponse } from 'src/app/shared/models/serviceResponse.model';
import { NestedTreeControl } from '@angular/cdk/tree';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { ProjectNode } from 'src/app/shared/models/projectNode';
import { Client } from 'src/app/shared/models/client';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { showNotification } from 'src/app/shared/components/app-notifications/app-notifications.component';

interface UserModel {
  id: string;
  name: string;
  email: string;
  permissions: string[];
  userRole: string;
  active: boolean;
  clientId?: string;
}

interface ClientModel {
  id: string;
  name: string;
  address: string;
  userUploaded: string;
  version: number;
  uploadTime: Date;
}

@Component({
  selector: 'app-user-management',
  templateUrl: './user-management.component.html',
  styleUrls: ['./user-management.component.scss']
})
export class UserManagementComponent implements OnInit {

  // Create user form
  newUser: RegisterUserRequest = {
    userName: '',
    email: '',
    userRole: '',
    permissions: [],
    clientId: '',
    usersId: []
  };

  // Client and project structure
  clientData?: Client;

  // Email validation error
  emailError: string = '';

  // Tree control for project structure
  treeControl = new NestedTreeControl<ProjectNode>(node => node.nodes);
  dataSource = new MatTreeNestedDataSource<ProjectNode>();

  // Selected nodes for permissions
  selectedNodes: string[] = [];

  // User list
  users: UserModel[] = [];
  isLoading: boolean = false;
  userListError: string = '';

  // User management section active tab
  activeSection: 'create' | 'list' | 'edit-permissions' = 'list';

  // Edit permissions
  selectedUser?: UserModel;
  isSavingPermissions: boolean = false;
  permissionsUpdateMessage: { type: 'success' | 'error', text: string } | null = null;

  clients: ClientModel[] = [];
  isLoadingClients: boolean = false;
  selectedClientId: string = '';
  filteredUsers: UserModel[] = [];

  // Add this property near the other class properties
  isAllSelected: boolean = false;

  private expandedNodes = new Set<any>();

  // Navigation properties
  showBackButton = false;
  selectedNodeForModelSelection: any = null;
  navigationStack: any[] = [];
  currentLevelNodes: any[] = [];
  modelSearchText = '';
  filteredModels: any[] = [];

  constructor(
    private backendApiService: BackendApiService,
    private authService: AuthService
  ) { }

  ngOnInit(): void {
    // Set default role if not admin
    if (!this.isAdmin()) {
      this.newUser.userRole = 'CustomerUser';
    }

    // Load clients if admin
    if (this.isAdmin()) {
      this.loadClients();
    }

    // Load client project structure
    this.loadClientProjectStructure();

    // Load users
    this.loadUsers();

    // Initialize navigation properties
    this.currentLevelNodes = this.getInitialNodes();
  }

  loadUsers(): void {
    this.isLoading = true;
    this.userListError = '';

    // Check if user is admin and has no client
    const isAdmin = this.authService.isAdmin();
    const clientData = this.authService.getClientDataStructure();
    const hasNoClient = !clientData || clientData === null;

    // If admin with no client, add a flag to request all users
    const getAllUsers = isAdmin && hasNoClient;

    this.backendApiService.getUsers().subscribe({
      next: (response: ServiceResponse<UserModel[]>) => {
        if (response.isSuccess) {
          this.users = response.result || [];
          this.filterUsersByClient();
        } else {
          const errorMessage = response.messages && response.messages.length > 0
            ? response.messages[0].content
            : 'Failed to load users';
          this.userListError = errorMessage || 'Unknown error';
        }
        this.isLoading = false;
      },
      error: (error: any) => {
        console.error('Error fetching users', error);
        this.userListError = 'Failed to load users. Please try again.';
        this.isLoading = false;
      }
    });
  }

  loadClientProjectStructure(): void {
    this.clientData = this.authService.getClientDataStructure();
    if (this.clientData && this.clientData.projectStructure) {
      this.dataSource.data = this.clientData.projectStructure;
      // Remove auto-expansion of nodes
      this.treeControl.collapseAll();
    }
  }

  setActiveSection(section: 'create' | 'list' | 'edit-permissions'): void {
    this.activeSection = section;

    // Reset any messages when changing sections
    this.permissionsUpdateMessage = null;

    // Reset selected nodes when switching to create section
    if (section === 'create') {
      this.selectedNodes = [];
      this.newUser.permissions = [];
    }
  }

  openEditPermissions(user: UserModel): void {
    this.selectedUser = {...user};
    this.selectedNodes = [...(user.permissions || [])];
    this.setActiveSection('edit-permissions');

    // Make sure tree is expanded and nodes are checked
    this.refreshTreeSelections();
  }

  deleteUser(user: UserModel): void {
    if (!user.id) {
      showNotification('error', 'Error', 'Invalid user ID', 5);
      return;
    }

    if (confirm(`Are you sure you want to delete user ${user.name}? This action cannot be undone.`)) {
      this.backendApiService.deleteUser(user.id).subscribe({
        next: (response: ServiceResponse<any>) => {
          if (response.isSuccess) {
            showNotification('success', 'Success', 'User deleted successfully', 5);
            this.loadUsers(); // Refresh the user list
          } else {
            const errorMessage = response.messages && response.messages.length > 0 && response.messages[0].content
              ? response.messages[0].content
              : 'Failed to delete user';
            showNotification('error', 'Error', errorMessage, 5);
          }
        },
        error: (error: any) => {
          console.error('Error deleting user', error);
          showNotification('error', 'Error', 'Failed to delete user. Please try again.', 5);
        }
      });
    }
  }

  refreshTreeSelections(): void {
    // Just update the selected nodes without expanding
    this.selectedNodes = [...(this.selectedUser?.permissions || [])];
  }

  saveUserPermissions(): void {
    if (!this.selectedUser) return;

    this.isSavingPermissions = true;
    this.permissionsUpdateMessage = null;

    this.backendApiService.updateUserPermissions(
      this.selectedUser.id,
      this.selectedNodes
    ).subscribe({
      next: (response: ServiceResponse<any>) => {
        if (response.isSuccess) {
          this.permissionsUpdateMessage = {
            type: 'success',
            text: 'Permissions updated successfully'
          };

          showNotification('success', 'Success', 'Permissions updated successfully', 5);

          // Update the user in the list
          this.loadUsers(); // Refresh the user list
        } else {
          const errorText = response.messages && response.messages.length > 0 && response.messages[0].content
            ? response.messages[0].content
            : 'Failed to update permissions';

          this.permissionsUpdateMessage = {
            type: 'error',
            text: errorText
          };

          showNotification('error', 'Error', errorText, 5);
        }
        this.isSavingPermissions = false;
      },
      error: (error: any) => {
        console.error('Error updating permissions', error);
        this.permissionsUpdateMessage = {
          type: 'error',
          text: 'Failed to update permissions. Please try again.'
        };

        showNotification('error', 'Error', 'Failed to update permissions. Please try again.', 5);
        this.isSavingPermissions = false;
      }
    });
  }

  cancelEditPermissions(): void {
    this.selectedUser = undefined;
    this.selectedNodes = [];
    this.setActiveSection('list');
  }

  getUserRoleDisplay(role: string): string {
    switch (role) {
      case 'Admin':
        return 'Admin';

      case 'User':
        return 'CustomerUser';

      case 'Customer':
        return 'CustomerAdmin';

      case 'Test User':
        return 'TestUser';

      case 'Model Exporter':
        return 'ModelExporter';

      default:
        return role;
    }
  }

  getPermissionsText(permissions: string[], userRole?: string): string {

    if (userRole) {

    // Check if user is Admin
    if (userRole.toLowerCase().includes('0')) {
      return 'Admin';
    }

    // Check if user is Customer
    if (userRole.toLowerCase() == '1' ) {
      return 'Customer';
    }

    if (userRole.toLowerCase() == '4') {
      return 'Model Exporter';
    }

    if (userRole.toLowerCase().includes('5')) {
      return 'Test User';
    }
  }


  if (!permissions || permissions.length === 0) {
    return 'No permissions assigned';
  }
    // If no specific role found, show the number of permissions
    if (permissions.length <= 2) {
      return permissions.join(', ');
    }

    return `${permissions.length} permissions assigned`;
  }

  isAdmin(): boolean {
    return this.authService.isAdmin();
  }

  validateEmail(email: string): boolean {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  }

  checkEmail(): void {
    if (!this.newUser.email) {
      this.emailError = 'Email is required';
    } else if (!this.validateEmail(this.newUser.email)) {
      this.emailError = 'Please enter a valid email address';
    } else {
      this.emailError = '';
    }
  }

  // Check if node has child nodes
  hasChild = (_: number, node: ProjectNode) => !!node.nodes && node.nodes.length > 0;

  hasNavigableNodes(): boolean {
    return this.currentLevelNodes?.some(node => node.nodes && node.nodes.length > 0) ?? false;
  }

  // Toggle selection of a node
  toggleNodeSelection(node: ProjectNode, isChecked: boolean): void {
    const nodePath = this.getNodePath(node);

    if (isChecked) {
      // Add node to selected nodes if not already included
      if (!this.selectedNodes.includes(nodePath)) {
        this.selectedNodes.push(nodePath);
      }

      // If node has children, select all children
      if (node.nodes && node.nodes.length > 0) {
        this.selectAllChildren(node);
      }
    } else {
      // Remove node from selected nodes
      this.selectedNodes = this.selectedNodes.filter(path => path !== nodePath);

      // Deselect all children
      if (node.nodes && node.nodes.length > 0) {
        this.deselectAllChildren(node);
      }
    }

    // Update permissions array if in create mode
    if (this.activeSection === 'create') {
      this.updatePermissions();
    }
  }

  // Get path for a node
  getNodePath(node: ProjectNode): string {
    let path = node.name + ':' + node.description;
    let currentNode = node;
    let parentNode = this.findParentNode(currentNode);

    while (parentNode) {
      path = parentNode.name + ':' + parentNode.description + '/' + path;
      currentNode = parentNode;
      parentNode = this.findParentNode(currentNode);
    }

    return path;
  }

  // Select all children of a node
  selectAllChildren(node: ProjectNode): void {
    if (node.nodes) {
      node.nodes.forEach((childNode: ProjectNode) => {
        this.selectedNodes.push(this.getNodePath(childNode));
        this.selectAllChildren(childNode);
      });
    }
  }

  // Deselect all children of a node
  deselectAllChildren(node: ProjectNode): void {
    if (node.nodes) {
      node.nodes.forEach((childNode: ProjectNode) => {
        const nodePath = this.getNodePath(childNode);
        this.selectedNodes = this.selectedNodes.filter(path => path !== nodePath);
        this.deselectAllChildren(childNode);
      });
    }
  }

  // Check if a node is selected
  isNodeSelected(node: ProjectNode): boolean {
    return this.selectedNodes.includes(this.getNodePath(node));
  }

  // Check if all children of a node are selected
  areAllChildrenSelected(node: ProjectNode): boolean {
    if (!node.nodes || node.nodes.length === 0) return false;
    return node.nodes.every((childNode: ProjectNode) => {
      const childPath = this.getNodePath(childNode);
      return this.selectedNodes.includes(childPath) && this.areAllChildrenSelected(childNode);
    });
  }

  // Check if some children of a node are selected
  areSomeChildrenSelected(node: ProjectNode): boolean {
    if (!node.nodes || node.nodes.length === 0) return false;
    return node.nodes.some((childNode: ProjectNode) => {
      const childPath = this.getNodePath(childNode);
      return this.selectedNodes.includes(childPath) || this.areSomeChildrenSelected(childNode);
    });
  }

  // Update permissions array in newUser
  updatePermissions(): void {
    this.newUser.permissions = [...this.selectedNodes];
  }

  onCheckboxChange(node: ProjectNode, event: MatCheckboxChange): void {
    this.toggleNodeSelection(node, event.checked);
  }

  onSubmit(): void {
    // Validate email before submitting
    this.checkEmail();
    if (this.emailError) {
      return;
    }

    this.createUser(this.newUser);
  }

  createUser(user: RegisterUserRequest): void {
    this.backendApiService.createUser(user).subscribe({
      next: (response: ServiceResponse<any>) => {
        console.log('User created successfully', response);
        // Reset form after successful creation
        this.resetForm();
        // Switch to the list view and refresh the user list
        this.activeSection = 'list';
        this.loadUsers();

        if (response.isSuccess) {
          showNotification('success', 'Success', 'User created successfully', 5);
        } else if (response.messages && response.messages.length > 0) {
          const errorMessage = response.messages[0].content || 'An error occurred';
          showNotification('error', 'Error', errorMessage, 5);
        }
      },
      error: (error: any) => {
        console.error('Error creating user', error);
        showNotification('error', 'Error', 'Failed to create user. Please try again.', 5);
      }
    });
  }

  resetForm(): void {
    this.newUser = {
      userName: '',
      email: '',
      userRole: this.isAdmin() ? '' : 'CustomerUser',
      permissions: [],
      clientId: '',
      usersId: []
    };
    this.selectedNodes = [];
    this.emailError = '';
  }

  loadClients(): void {
    this.isLoadingClients = true;
    this.backendApiService.getAllClients().subscribe({
      next: (response: ServiceResponse<ClientModel[]>) => {
        if (response.isSuccess && response.result) {
          this.clients = response.result;
        }
        this.isLoadingClients = false;
      },
      error: (error: any) => {
        console.error('Error loading clients', error);
        this.isLoadingClients = false;
      }
    });
  }

  onClientFilterChange(): void {
    this.filterUsersByClient();
  }

  filterUsersByClient(): void {
    if (!this.selectedClientId) {
      this.filteredUsers = [...this.users];
    } else if (this.selectedClientId === 'no-client') {
      this.filteredUsers = this.users.filter(user => !user.clientId);
    } else {
      this.filteredUsers = this.users.filter(user => user.clientId === this.selectedClientId);
    }
  }

  // Add this method before selectAllNodes
  getSelectAllButtonText(): string {
    return this.isAllSelected ? 'Deselect All' : 'Select All';
  }

  selectAllNodes(): void {
    // Check if all nodes are currently selected
    const allNodesSelected = this.dataSource.data.every(node => this.isNodeSelected(node));

    if (allNodesSelected) {
      // If all nodes are selected, deselect all
      this.selectedNodes = [];
      this.isAllSelected = false;
    } else {
      // If not all nodes are selected, select all
      this.selectedNodes = [];

      // Helper function to recursively select all nodes
      const selectAllRecursive = (nodes: ProjectNode[]) => {
        nodes.forEach(node => {
          // Add current node to selection
          this.selectedNodes.push(this.getNodePath(node));

          // If node has children, recursively select them
          if (node.nodes && node.nodes.length > 0) {
            selectAllRecursive(node.nodes);
          }
        });
      };

      // Start selection from root nodes
      if (this.dataSource.data) {
        selectAllRecursive(this.dataSource.data);
      }
      this.isAllSelected = true;
    }
  }

  isExpanded(node: any): boolean {
    return this.expandedNodes.has(node);
  }

  toggleNode(node: any, event: Event): void {
    event.stopPropagation();
    if (this.expandedNodes.has(node)) {
      this.expandedNodes.delete(node);
    } else {
      // Close any other expanded nodes first
      this.expandedNodes.clear();
      this.expandedNodes.add(node);
    }
  }

  collapseNode(node: any, event: Event): void {
    event.stopPropagation();
    this.expandedNodes.delete(node);
  }

  removeSelectedCard(nodePath: string): void {
    // Remove the node from selectedNodes
    this.selectedNodes = this.selectedNodes.filter(path => path !== nodePath);

    // Update permissions array if in create mode
    if (this.activeSection === 'create') {
      this.updatePermissions();
    }

    // Find the node in the tree and update its checkbox state
    const updateNodeState = (nodes: ProjectNode[]) => {
      for (const node of nodes) {
        if (this.getNodePath(node) === nodePath) {
          // If the node has children, deselect them too
          if (node.nodes && node.nodes.length > 0) {
            this.deselectAllChildren(node);
          }
          break;
        }
        if (node.nodes && node.nodes.length > 0) {
          updateNodeState(node.nodes);
        }
      }
    };

    if (this.dataSource.data) {
      updateNodeState(this.dataSource.data);
    }
  }

  // Navigation methods
  navigateToNode(node: any) {
    console.log('Navigating to node:', node);
    if (node.nodes && node.nodes.length > 0) {
      // If the node has children that are models (no further children)
      if (!node.nodes.some((child: ProjectNode) => child.nodes && child.nodes.length > 0)) {
        // These are models, so we'll show them directly
        this.currentLevelNodes = node.nodes;
        this.showBackButton = true; // Show back button when viewing models
      } else {
        // These are regions, so we'll navigate to their children
        this.navigationStack.push(node);
        this.currentLevelNodes = node.nodes;
        this.showBackButton = true;
      }
    }
  }

  navigateBack() {
    if (this.navigationStack.length > 0) {
      const previousNode = this.navigationStack.pop();
      if (previousNode) {
        this.currentLevelNodes = previousNode.nodes;
      }
      this.showBackButton = this.navigationStack.length > 0;
    } else {
      // If no navigation stack, go back to initial nodes
      this.currentLevelNodes = this.getInitialNodes();
      this.showBackButton = false;
    }
  }

  getInitialNodes() {
    // Return the top-level nodes (Regions)
    return this.dataSource.data;
  }

  getIcon(type: string): string {
    // Return the appropriate icon path based on the type
    return `/assets/icons/${type.toLowerCase()}.png`;
  }

  // Model selection methods
  filterModels() {
    console.log('Selected node for model selection:', this.selectedNodeForModelSelection);
    if (!this.selectedNodeForModelSelection) {
      this.filteredModels = [];
      return;
    }

    const models = this.selectedNodeForModelSelection.nodes || [];
    console.log('Available models:', models);
    if (!this.modelSearchText) {
      this.filteredModels = models;
    } else {
      this.filteredModels = models.filter(
        (model: any) => model.description.toLowerCase().includes(this.modelSearchText.toLowerCase())
      );
    }
    console.log('Filtered models:', this.filteredModels);
  }

  isModelSelected(model: any): boolean {
    return this.selectedNodes.includes(model.id);
  }

  toggleModelSelection(model: any, checked: boolean) {
    if (checked) {
      this.addModelToSelection(model);
    } else {
      this.removeModelFromSelection(model);
    }
  }

  addModelToSelection(model: any) {
    if (!this.selectedNodes.includes(model.id)) {
      this.selectedNodes.push(model.id);
    }
  }

  removeModelFromSelection(model: any) {
    const index = this.selectedNodes.indexOf(model.id);
    if (index > -1) {
      this.selectedNodes.splice(index, 1);
    }
  }

  // Helper method to find parent node
  private findParentNode(node: ProjectNode): ProjectNode | null {
    const findParent = (nodes: ProjectNode[]): ProjectNode | null => {
      for (const currentNode of nodes) {
        if (currentNode.nodes && currentNode.nodes.includes(node)) {
          return currentNode;
        }
        if (currentNode.nodes) {
          const found = findParent(currentNode.nodes);
          if (found) return found;
        }
      }
      return null;
    };

    return findParent(this.dataSource.data);
  }

  findNodeByPath(path: string): ProjectNode | undefined {
    const findNode = (nodes: ProjectNode[]): ProjectNode | undefined => {
      for (const node of nodes) {
        if (this.getNodePath(node) === path) {
          return node;
        }
        if (node.nodes && node.nodes.length > 0) {
          const found = findNode(node.nodes);
          if (found) return found;
        }
      }
      return undefined;
    };

    return this.dataSource.data ? findNode(this.dataSource.data) : undefined;
  }

  getNodeDisplayName(nodePath: string): string {
    const parts = nodePath.split('/');
    if (parts.length >= 2) {
      // Get the region (first part) and type (last part)
      const region = parts[0].split(':')[1]; // Get description after the colon
      const type = parts[parts.length - 1].split(':')[1]; // Get description after the colon
      return `${region}/${type}`;
    }
    // Fallback to just showing the description if path format is unexpected
    return nodePath.split(':')[1] || nodePath;
  }
}
